import {isUuid} from '@benchling/js-utils';
import {v4 as uuidv4} from 'uuid';

import * as analytics from 'analytics/Analytics';
import {ArchiveRecord} from 'archive/ArchiveConstants';
import BenchlingConfig from 'BenchlingConfig';
import {RESULTS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import exportWithNotification from 'exports/exportWithNotification';
import {readFragmentFromCache, writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import fetchAllPaginated from 'lib/fetchAllPaginated';
import {ACTION_CREATE_RUN} from 'notebook/store/VersioningConstants';
import {ActionFunctionContext} from 'notebook/structured-tables/action-functions/ActionFunctionContext';
import {
  abortAction,
  ActionResultType,
  DoPerformAction,
  performServerSideWriteAction,
} from 'notebook/structured-tables/action-functions/PerformActionFunctions';
import notifications, {GenericResponseJSON, Response} from 'notifications';
import {BenchlingDispatch, Thunk} from 'redux-stores/Store';
import {AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import {EntriesByRunId} from 'runs/inbox/RunsInboxModalConstants';
import {refetchRun} from 'runs/withMaybeRunFetching';
import {TagUpdateObject} from 'schemas/tags/TagUtil';
import {populateSearchParamWithDefaultSort} from 'search/getSortsOrDefault';
import {SearchParams, SearchType} from 'search/SearchConstants';
import {Optional} from 'util/Types';

import {ResultGraphQL} from '../../../graphql-fragments/results/__generated/ResultGraphQL';
import {AssayResult, AssayRun, isOversized} from './AssayRecordConstants';
import * as AssayRecordDAO from './AssayRecordDAO';

export enum AssayRecordActionType {
  ASSAY_DATA_ATTACHMENTS_SERVER_BOOTSTRAP = 'ASSAY_DATA_ATTACHMENTS_SERVER_BOOTSTRAP',
  ASSAY_RUNS_FETCH_COMPLETED = 'ASSAY_RUNS_FETCH_COMPLETED',
  ASSAY_RUN_FETCH_DATA_MISSING = 'ASSAY_RUN_FETCH_DATA_MISSING',
  ASSAY_RESULTS_FETCH_BY_RUN_STARTED = 'ASSAY_RESULTS_FETCH_BY_RUN_STARTED',
  ASSAY_RESULTS_FETCH_BY_RUN_PROGRESSED = 'ASSAY_RESULTS_FETCH_BY_RUN_PROGRESSED',
  ASSAY_RESULTS_FETCH_BY_RUN_COMPLETED = 'ASSAY_RESULTS_FETCH_BY_RUN_COMPLETED',
  ASSAY_RESULTS_FETCH_BY_RUN_DATA_MISSING = 'ASSAY_RESULTS_FETCH_BY_RUN_DATA_MISSING',
  RUN_RESULTS_COUNTED_BY_SCHEMA = 'RUN_RESULTS_COUNTED_BY_SCHEMA',
  ASSAY_RESULTS_FETCH_COMPLETED = 'ASSAY_RESULTS_FETCH_COMPLETED',
  ASSAY_RESULTS_FETCH_DATA_MISSING = 'ASSAY_RESULTS_FETCH_DATA_MISSING',
  ASSAY_RUN_SEARCH_STARTED = 'ASSAY_RUN_SEARCH_STARTED',
  ASSAY_RUN_FETCH_MORE_RESULTS_STARTED = 'ASSAY_RUN_FETCH_MORE_RESULTS_STARTED',
  ASSAY_RUN_SEARCH_COMPLETED = 'ASSAY_RUN_SEARCH_COMPLETED',
  ASSAY_RESULT_SEARCH_STARTED = 'ASSAY_RESULT_SEARCH_STARTED',
  ASSAY_RESULT_FETCH_MORE_RESULTS_STARTED = 'ASSAY_RESULT_FETCH_MORE_RESULTS_STARTED',
  ASSAY_RESULT_SEARCH_COMPLETED = 'ASSAY_RESULT_SEARCH_COMPLETED',
  ASSAY_RUN_UPDATE_COMPLETED = 'ASSAY_RUN_UPDATE_COMPLETED',
  LEGACY_ASSAY_RUN_SEARCH_COMPLETED = 'LEGACY_ASSAY_RUN_SEARCH_COMPLETED', // TODO(BNCH-57692): Remove once ENABLE_SIGHTGLASS_RUNS_SEARCH is enabled everywhere
}

export type AssayRecordAction =
  | ServerBootstrapAttachmentsAction
  | AssayRunsFetchCompletedAction
  | AssayRunFetchDataMissingAction
  | AssayResultsFetchByRunStartedAction
  | AssayResultsFetchByRunProgressedAction
  | AssayResultsFetchByRunCompletedAction
  | AssayResultsFetchByRunDataMissingAction
  | RunResultsCountedBySchemaAction
  | AssayResultsFetchCompletedAction
  | AssayResultsFetchDataMissingAction
  | AssayRunSearchStartedAction
  | AssayRunFetchMoreResultsStartedAction
  | AssayRunSearchCompletedAction
  | AssayResultSearchStartedAction
  | AssayResultFetchMoreResultsStartedAction
  | AssayResultSearchCompletedAction
  | AssayRunUpdateCompletedAction
  | LegacyAssayRunSearchCompletedAction;

interface ServerBootstrapAttachmentsAction {
  type: AssayRecordActionType.ASSAY_DATA_ATTACHMENTS_SERVER_BOOTSTRAP;
  assayRunsById: {[assayRunId: string]: AssayRun};
  assayResultsById: {[assayResultId: string]: AssayResult};
  assayResultIdsByRunId: {[runId: string]: Array<string>};
}

export function serverBootstrapAttachments({
  assayRunsById,
  assayResultsById,
  assayResultIdsByRunId,
}: {
  assayRunsById: {[assayRunId: string]: AssayRun};
  assayResultsById: {[assayResultId: string]: AssayResult};
  assayResultIdsByRunId: {[runId: string]: Array<string>};
}): ServerBootstrapAttachmentsAction {
  return {
    type: AssayRecordActionType.ASSAY_DATA_ATTACHMENTS_SERVER_BOOTSTRAP,
    assayRunsById,
    assayResultsById,
    assayResultIdsByRunId,
  };
}

function invalidIdFromAPIError(errorResp: Response): string | null {
  if (
    errorResp.status >= 400 &&
    errorResp.status < 500 &&
    (errorResp.responseJSON as GenericResponseJSON).invalidId
  ) {
    return (errorResp.responseJSON as GenericResponseJSON).invalidId;
  }
  return null;
}

interface AssayRunsFetchCompletedAction {
  type: AssayRecordActionType.ASSAY_RUNS_FETCH_COMPLETED;
  assayRunsById: {[runId: string]: AssayRun};
}

export function assayRunsFetchCompleted(assayRunsById: {
  [runId: string]: AssayRun;
}): AssayRunsFetchCompletedAction {
  return {
    type: AssayRecordActionType.ASSAY_RUNS_FETCH_COMPLETED,
    assayRunsById,
  };
}

interface AssayRunFetchDataMissingAction {
  type: AssayRecordActionType.ASSAY_RUN_FETCH_DATA_MISSING;
  assayRunId: string;
}

export function assayRunFetchDataMissing(assayRunId: string): AssayRunFetchDataMissingAction {
  return {
    type: AssayRecordActionType.ASSAY_RUN_FETCH_DATA_MISSING,
    assayRunId,
  };
}

export function fetchAssayRuns(assayRunIds: Array<string>): Thunk<Promise<void>> {
  return (dispatch) =>
    AssayRecordDAO.fetchAssayRuns(assayRunIds).then(
      ({assayRunsById}) => {
        dispatch(assayRunsFetchCompleted(assayRunsById));
      },
      (errorResp) => {
        const invalidId = invalidIdFromAPIError(errorResp);
        if (invalidId) {
          dispatch(assayRunFetchDataMissing(invalidId));
        } else {
          notifications.notifyError(errorResp);
        }
      }
    );
}

interface AssayResultsFetchByRunStartedAction {
  type: AssayRecordActionType.ASSAY_RESULTS_FETCH_BY_RUN_STARTED;
  assayRunId: string;
}

export function assayResultsFetchByRunStarted(assayRunId: string): AssayResultsFetchByRunStartedAction {
  return {
    type: AssayRecordActionType.ASSAY_RESULTS_FETCH_BY_RUN_STARTED,
    assayRunId,
  };
}

interface AssayResultsFetchByRunProgressedAction {
  type: AssayRecordActionType.ASSAY_RESULTS_FETCH_BY_RUN_PROGRESSED;
  assayRunId: string;
  assayResults: Array<AssayResult>;
}

export function assayResultsFetchByRunProgressed(
  assayRunId: string,
  assayResults: Array<AssayResult>
): AssayResultsFetchByRunProgressedAction {
  return {
    type: AssayRecordActionType.ASSAY_RESULTS_FETCH_BY_RUN_PROGRESSED,
    assayRunId,
    assayResults,
  };
}

interface AssayResultsFetchByRunCompletedAction {
  type: AssayRecordActionType.ASSAY_RESULTS_FETCH_BY_RUN_COMPLETED;
  assayRunId: string;
  assayResults: Array<AssayResult>;
}

export function assayResultsFetchByRunCompleted(
  assayRunId: string,
  assayResults: Array<AssayResult>
): AssayResultsFetchByRunCompletedAction {
  return {
    type: AssayRecordActionType.ASSAY_RESULTS_FETCH_BY_RUN_COMPLETED,
    assayRunId,
    assayResults,
  };
}

interface AssayResultsFetchByRunDataMissingAction {
  type: AssayRecordActionType.ASSAY_RESULTS_FETCH_BY_RUN_DATA_MISSING;
  assayRunId: string;
}

export function assayResultsFetchByRunDataMissing(
  assayRunId: string
): AssayResultsFetchByRunDataMissingAction {
  return {
    type: AssayRecordActionType.ASSAY_RESULTS_FETCH_BY_RUN_DATA_MISSING,
    assayRunId,
  };
}

interface RunResultsCountedBySchemaAction {
  type: AssayRecordActionType.RUN_RESULTS_COUNTED_BY_SCHEMA;
  assayRunId: string;
  resultCountBySchemaId: {[schemaId: string]: number};
}

export function runResultsCountedBySchema({
  assayRunId,
  resultCountBySchemaId,
}: {
  assayRunId: string;
  resultCountBySchemaId: {[schemaId: string]: number};
}): RunResultsCountedBySchemaAction {
  return {
    type: AssayRecordActionType.RUN_RESULTS_COUNTED_BY_SCHEMA,
    assayRunId,
    resultCountBySchemaId,
  };
}

export function countRunResultsBySchema({
  assayRunId,
}: {
  assayRunId: string;
}): Thunk<Promise<{[schemaId: string]: number}>> {
  return async (dispatch) => {
    const response = await AssayRecordDAO.countResultsBySchema({assayRunId});
    const {resultCountBySchemaId} = response;
    dispatch(runResultsCountedBySchema({assayRunId, resultCountBySchemaId}));
    return resultCountBySchemaId;
  };
}

async function fetchNextResultsByRun(
  dispatch: BenchlingDispatch,
  assayRunId: string,
  nextToken: string | undefined,
  {excludedSchemaIds}: {excludedSchemaIds: Array<string>}
): Promise<{assayResults: Array<AssayResult>; nextToken: string} | null> {
  try {
    return await AssayRecordDAO.fetchAssayResultsByRun(assayRunId, nextToken, {
      excludedSchemaIds,
    });
  } catch (errorResp) {
    if (errorResp.status >= 400 && errorResp.status < 500) {
      dispatch(assayResultsFetchByRunDataMissing(assayRunId));
    } else {
      throw errorResp;
    }
    return null;
  }
}

export function fetchAssayResultsByRun(assayRunId: string): Thunk<Promise<void>> {
  return async (dispatch) => {
    const resultCountBySchemaId = await dispatch(countRunResultsBySchema({assayRunId}));
    const oversizedSchemaIds = Object.keys(resultCountBySchemaId).filter((key) =>
      isOversized(resultCountBySchemaId[key])
    );

    dispatch(assayResultsFetchByRunStarted(assayRunId));
    let assayResults: Array<AssayResult> = [];
    await fetchAllPaginated<{assayResults: Array<AssayResult>; nextToken: string}>(
      (nextToken) =>
        fetchNextResultsByRun(dispatch, assayRunId, nextToken, {excludedSchemaIds: oversizedSchemaIds}),
      (response) => {
        assayResults = assayResults.concat(response.assayResults);
        dispatch(assayResultsFetchByRunProgressed(assayRunId, assayResults));
        return response.assayResults;
      }
    );
    dispatch(assayResultsFetchByRunCompleted(assayRunId, assayResults));
  };
}

interface AssayResultsFetchCompletedAction {
  type: AssayRecordActionType.ASSAY_RESULTS_FETCH_COMPLETED;
  assayResultsById: {[resultId: string]: AssayResult};
}

export function assayResultsFetchCompleted(assayResultsById: {
  [resultId: string]: AssayResult;
}): AssayResultsFetchCompletedAction {
  return {
    type: AssayRecordActionType.ASSAY_RESULTS_FETCH_COMPLETED,
    assayResultsById,
  };
}

interface AssayResultsFetchDataMissingAction {
  type: AssayRecordActionType.ASSAY_RESULTS_FETCH_DATA_MISSING;
  assayResultId: string;
}

export function assayResultsFetchDataMissing(assayResultId: string): AssayResultsFetchDataMissingAction {
  return {
    type: AssayRecordActionType.ASSAY_RESULTS_FETCH_DATA_MISSING,
    assayResultId,
  };
}

export function fetchAssayResults(assayResultIds: Array<string>): Thunk<void> {
  return (dispatch) =>
    AssayRecordDAO.fetchAssayResults(assayResultIds).then(
      ({assayResultsById}) => {
        dispatch(assayResultsFetchCompleted(assayResultsById));
      },
      (errorResp) => {
        const invalidId = invalidIdFromAPIError(errorResp);
        if (invalidId) {
          dispatch(assayResultsFetchDataMissing(invalidId));
        } else {
          notifications.notifyError(errorResp);
        }
      }
    );
}

interface AssayRunSearchStartedAction {
  type: AssayRecordActionType.ASSAY_RUN_SEARCH_STARTED;
  requestId: string;
}

export function assayRunSearchStarted({requestId}: {requestId: string}): AssayRunSearchStartedAction {
  return {
    type: AssayRecordActionType.ASSAY_RUN_SEARCH_STARTED,
    requestId,
  };
}

interface AssayRunFetchMoreResultsStartedAction {
  type: AssayRecordActionType.ASSAY_RUN_FETCH_MORE_RESULTS_STARTED;
  requestId: string;
}

export function assayRunFetchMoreResultsStarted({
  requestId,
}: {
  requestId: string;
}): AssayRunFetchMoreResultsStartedAction {
  return {
    type: AssayRecordActionType.ASSAY_RUN_FETCH_MORE_RESULTS_STARTED,
    requestId,
  };
}

interface AssayRunSearchCompletedAction {
  type: AssayRecordActionType.ASSAY_RUN_SEARCH_COMPLETED;
  requestId: string;
  assayRuns: Array<AssayRun>;
  entriesByRunId: EntriesByRunId;
  assayResultSchemasByRunId: {[warehouseId: string]: Array<AssaySchema>};
  offset: number;
  total: number;
}

export function assayRunSearchCompleted({
  requestId,
  assayRuns,
  entriesByRunId,
  assayResultSchemasByRunId,
  offset,
  total,
}: {
  requestId: string;
  assayRuns: Array<AssayRun>;
  entriesByRunId: EntriesByRunId;
  assayResultSchemasByRunId: {[warehouseId: string]: Array<AssaySchema>};
  offset: number;
  total: number;
}): AssayRunSearchCompletedAction {
  return {
    type: AssayRecordActionType.ASSAY_RUN_SEARCH_COMPLETED,
    requestId,
    assayRuns,
    entriesByRunId,
    assayResultSchemasByRunId,
    offset,
    total,
  };
}

export function searchAssayRuns({
  searchParams,
  level,
}: {
  searchParams: SearchParams;
  level: string;
}): Thunk<void> {
  return (dispatch) => {
    const requestId = uuidv4();

    if (searchParams.offset !== 0) {
      dispatch(assayRunFetchMoreResultsStarted({requestId}));
    } else {
      dispatch(assayRunSearchStarted({requestId}));
    }

    const searchParamsWithDefault = populateSearchParamWithDefaultSort(searchParams, SearchType.RUN);

    AssayRecordDAO.listAssayRuns(searchParamsWithDefault, level).then(
      ({assayRuns, entriesByRunId, assayResultSchemasByRunId, total}) => {
        dispatch(
          assayRunSearchCompleted({
            requestId,
            assayRuns,
            entriesByRunId,
            assayResultSchemasByRunId,
            offset: searchParamsWithDefault.offset + searchParamsWithDefault.limit,
            total,
          })
        );
      },
      notifications.notifyError
    );
  };
}

interface LegacyAssayRunSearchCompletedAction {
  type: AssayRecordActionType.LEGACY_ASSAY_RUN_SEARCH_COMPLETED;
  requestId: string;
  assayRuns: Array<AssayRun>;
  entriesByRunId: EntriesByRunId;
  assayResultSchemasByRunId: {[warehouseId: string]: Array<AssaySchema>};
  nextToken: string | null;
}

// TODO(BNCH-57692): Remove once ENABLE_SIGHTGLASS_RUNS_SEARCH is enabled everywhere
export function legacyAssayRunSearchCompleted({
  requestId,
  assayRuns,
  entriesByRunId,
  assayResultSchemasByRunId,
  nextToken,
}: {
  requestId: string;
  assayRuns: Array<AssayRun>;
  entriesByRunId: EntriesByRunId;
  assayResultSchemasByRunId: {[warehouseId: string]: Array<AssaySchema>};
  nextToken: string | null;
}): LegacyAssayRunSearchCompletedAction {
  return {
    type: AssayRecordActionType.LEGACY_ASSAY_RUN_SEARCH_COMPLETED,
    requestId,
    assayRuns,
    entriesByRunId,
    assayResultSchemasByRunId,
    nextToken,
  };
}

// TODO(BNCH-57692): Remove once ENABLE_SIGHTGLASS_RUNS_SEARCH is enabled everywhere
export function legacySearchAssayRuns({
  schemaId,
  nextToken,
  level,
  originType,
  isAvailable,
  isArchived,
  query,
}: {
  schemaId: string;
  nextToken: Optional<string>;
  level: string;
  originType?: string;
  isAvailable: boolean;
  isArchived: boolean;
  query: string;
}): Thunk<void> {
  return (dispatch) => {
    const requestId = uuidv4();

    if (nextToken) {
      dispatch(assayRunFetchMoreResultsStarted({requestId}));
    } else {
      dispatch(assayRunSearchStarted({requestId}));
    }

    AssayRecordDAO.legacyListAssayRuns({
      schemaId,
      nextToken,
      level,
      originType,
      isAvailable,
      isArchived,
      query,
    }).then(({assayRuns, entriesByRunId, assayResultSchemasByRunId, nextToken: newNextToken}) => {
      dispatch(
        legacyAssayRunSearchCompleted({
          requestId,
          assayRuns,
          entriesByRunId,
          assayResultSchemasByRunId,
          nextToken: newNextToken,
        })
      );
    }, notifications.notifyError);
  };
}

interface AssayResultSearchStartedAction {
  type: AssayRecordActionType.ASSAY_RESULT_SEARCH_STARTED;
  requestId: string;
}

export function assayResultSearchStarted({requestId}: {requestId: string}): AssayResultSearchStartedAction {
  return {
    type: AssayRecordActionType.ASSAY_RESULT_SEARCH_STARTED,
    requestId,
  };
}

interface AssayResultFetchMoreResultsStartedAction {
  type: AssayRecordActionType.ASSAY_RESULT_FETCH_MORE_RESULTS_STARTED;
  requestId: string;
}

export function assayResultFetchMoreResultsStarted({
  requestId,
}: {
  requestId: string;
}): AssayResultFetchMoreResultsStartedAction {
  return {
    type: AssayRecordActionType.ASSAY_RESULT_FETCH_MORE_RESULTS_STARTED,
    requestId,
  };
}

interface AssayResultSearchCompletedAction {
  type: AssayRecordActionType.ASSAY_RESULT_SEARCH_COMPLETED;
  requestId: string;
  assayResults: Array<AssayResult>;
  nextToken: string;
}

export function assayResultSearchCompleted({
  requestId,
  assayResults,
  nextToken,
}: {
  requestId: string;
  assayResults: Array<AssayResult>;
  nextToken: string;
}): AssayResultSearchCompletedAction {
  return {
    type: AssayRecordActionType.ASSAY_RESULT_SEARCH_COMPLETED,
    requestId,
    assayResults,
    nextToken,
  };
}

export function searchAssayResults({
  schemaId,
  nextToken,
}: {
  schemaId: string;
  nextToken: string;
}): Thunk<void> {
  return (dispatch) => {
    const requestId = uuidv4();

    if (nextToken) {
      dispatch(assayResultFetchMoreResultsStarted({requestId}));
    } else {
      dispatch(assayResultSearchStarted({requestId}));
    }

    AssayRecordDAO.listAssayResults({schemaId, nextToken}).then(({assayResults, nextToken: newNextToken}) => {
      dispatch(
        assayResultSearchCompleted({
          requestId,
          assayResults,
          nextToken: newNextToken,
        })
      );
    }, notifications.notifyError);
  };
}

interface AssayRunUpdateCompletedAction {
  type: AssayRecordActionType.ASSAY_RUN_UPDATE_COMPLETED;
  assayRun: AssayRun;
}

export function assayRunUpdateCompleted(assayRun: AssayRun): AssayRunUpdateCompletedAction {
  return {
    type: AssayRecordActionType.ASSAY_RUN_UPDATE_COMPLETED,
    assayRun,
  };
}

export function updateAssayRun(
  assayRunId: string,
  updateAssayRunData: {
    tags: TagUpdateObject;
    connectionId: string | null;
    equipmentId: string | null;
    studyIds: Array<string>;
  }
): Thunk<Promise<void>> {
  return async (dispatch) => {
    const response = await AssayRecordDAO.updateAssayRun(assayRunId, updateAssayRunData);
    const {assayRun} = response;
    await refetchRun(assayRunId, assayRun);
    dispatch(assayRunUpdateCompleted(assayRun));
  };
}

export function exportResults(
  schemaId: string,
  {
    bioentityId,
    requestId,
    storableId,
    studyId,
  }: {bioentityId?: string; requestId?: string; storableId?: string; studyId?: string}
): void {
  exportWithNotification(async () => {
    const response = await AssayRecordDAO.exportResults(schemaId, {
      bioentityId,
      requestId,
      storableId,
      studyId,
    });
    return {url: response!.url};
  });
}

export function exportRunResults(assayRunId: string, resultSchemaId: string): void {
  exportWithNotification(async () => {
    const response = await AssayRecordDAO.exportRunResults(assayRunId, resultSchemaId);
    return {url: response!.url};
  });
}

export async function fetchOversizedNLADTable(
  structuredTableId: string
): Promise<AssayRecordDAO.OversizedNLADTable> {
  const response = await AssayRecordDAO.fetchOversizedNLADTable(structuredTableId);
  return response;
}

export function writeResultArchiveRecordsToCache(archiveRecordsByItemId: {
  [itemId: string]: ArchiveRecord;
}): void {
  if (BenchlingConfig.ELN_ENABLE_ARCHIVING_RESULTS_WITH_ENTRY) {
    const fragmentData: Array<ResultGraphQL> = [];
    Object.entries(archiveRecordsByItemId).forEach(([id, archiveRecord]) => {
      if (isUuid(id)) {
        const data = readFragmentFromCache(getFragmentWrapper('Result'), id);
        if (data == null) {
          return;
        }
        fragmentData.push({
          ...data,
          archiveRecord:
            archiveRecord != null ? {__typename: 'ArchiveRecordNode', purpose: archiveRecord.purpose} : null,
        });
      }
    });
    if (fragmentData.length > 0) {
      writeFragmentsToCache(getFragmentWrapper('Result'), fragmentData);
      analytics.trackEvent(RESULTS_ANALYTICS_ACTIONS.ARCHIVED_ENTRY_WITH_RESULTS, {
        resultsArchived: fragmentData.length,
      });
    }
  }
}

/**
 * Calls the back-end and asks it to create a new AssayRun that should be
 * attached to a NoteLinkedAssayData contained within the entry. The
 * NoteLinkedAssayData is matched by the provenanceUuid which is shared across
 * all entry versions. The AssayRun is created with the schema provided and the
 * given tags. After the back-end request succeeds, a new entry version is
 * created so this function will refresh the current entry to get the latest
 * version.
 *
 * entryOrTemplateId - the api identifier of the entry, stage_entry or template containing the NLAD
 * noteLinkedAssayDataProvenanceUuid - the provenance UUID of the NLAD in the entry
 * assayRunSchemaId - the AssayRun schema api identifier we want the assayRun created with
 * tags - the tags we want to assign to the assayRun
 * backfillProvenanceUuidFn - a function that should be called if we need to backfill the provenanceUuid
 *                            of the NLAD before attempting to create the AssayRun
 * actionFunctionContext - the entry or document context within which the AssayRun is being created
 */
export async function createAssayRunAndPatchEntry({
  entryOrTemplateId,
  noteLinkedAssayDataProvenanceUuid,
  assayRunSchemaId,
  tags,
  connectionId,
  equipmentId,
  studyIds,
  backfillProvenanceUuidFn,
  actionFunctionContext,
}: {
  entryOrTemplateId: string;
  noteLinkedAssayDataProvenanceUuid: string;
  assayRunSchemaId: string;
  tags: TagUpdateObject;
  connectionId: string | null;
  equipmentId: string | null;
  studyIds: Array<string>;
  backfillProvenanceUuidFn?: () => void;
  actionFunctionContext: ActionFunctionContext;
}): Promise<void> {
  const doPatch: DoPerformAction<null> = async () => {
    try {
      await AssayRecordDAO.createAssayRunAndPatchEntry(
        entryOrTemplateId,
        noteLinkedAssayDataProvenanceUuid,
        assayRunSchemaId,
        tags,
        connectionId,
        equipmentId,
        studyIds,
        actionFunctionContext
      );
      return {resultType: ActionResultType.SUCCESS, responseData: null};
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      notifications.notifyError(e);
      return abortAction();
    }
  };
  await performServerSideWriteAction({
    entryId: entryOrTemplateId,
    loadingScreenMessage: 'Inserting run...',
    autoSaverAction: ACTION_CREATE_RUN,
    doPreSaveAction: () =>
      new Promise<void>((resolve) => {
        if (backfillProvenanceUuidFn) {
          backfillProvenanceUuidFn();
        }
        resolve();
      }),
    doPerformAction: doPatch,
    analyticsActionForPerfTracking: 'assay_run_create',
  });
}
